import { Button, DynamicContainer, Form, FormContext, FormSection } from '@hai/ui-react';
import { ButtonStateType } from '@hai/ui-react/dist/types';
import { FormikProps } from 'formik';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClientWithoutHandler } from 'src/js/api';
import { APIPath } from 'src/js/api/route-path-index';
import { FormSelect } from 'src/js/component/base/form/form-select';
import { FormSwitch } from 'src/js/component/base/form/form-switch';
import { FormTextfield } from 'src/js/component/base/form/form-textfield';
import { useStores } from 'src/js/hook/use-stores';
import { notificationHandler } from 'src/js/notification/notification-handler';
import { SecurityTabContent } from 'src/js/pages/security/security-tab-content';
import { validateFormContext } from 'src/js/validator/validator';

import {
  AuditModel,
  AuditResponse,
  auditStateToIcon,
  AuditTransport,
  AuditTrustServer,
  auditValidationSchema,
} from './audit-models';
import { FormValue } from 'src/js/component/base/form/form-value';
import { toTitleCase } from 'src/js/util/global-util';
import { useAdaptiveFetch } from 'src/js/hook/use-adaptive-fetch';
import auditModel, { fetchAudit } from './audit-store';
import { changes } from 'src/js/util/formik-helper';

export const Audit: React.FunctionComponent = () => {
  const { t } = useTranslation();

  const { sessionStore } = useStores();
  const formRef = useRef(null);

  const [buttonState, setButtonState] = useState<ButtonStateType>(undefined);
  const [model, setModel] = useState<AuditModel>();
  const [auditState, setAuditState] = useState<string>();
  const [errorReason, setErrorReason] = useState<string>();
  const [iconState, setIconState] = useState(auditStateToIcon(''));
  useAdaptiveFetch(
    () =>
      fetchAudit(t).then((res) => {
        setModel(auditModel);
        setAuditState(auditModel.state);
        setIconState(auditStateToIcon(auditModel.state));
        setErrorReason(auditModel.errorReason);
        return res;
      }),
    5000,
  );

  const shouldReloadData = () => {
    //mutate();
  };

  const handleSubmit = (values: any) => {
    setButtonState('pending');
    return apiClientWithoutHandler.genericController
      .put<AuditResponse>(APIPath.audit, values)
      .then((res) => {
        notificationHandler(
          res,
          t('security.audit.notifications.changeSuccessMsg'),
          t('security.audit.notifications.changeErrorMsg'),
          t,
        );
        return res;
      })
      .then((res) => {
        setButtonState(undefined);
        if (res.ok) {
          shouldReloadData();
          formRef.current.resetForm({ values: res.data });
        }
        return res;
      });
  };

  return (
    <SecurityTabContent name={t('security.audit.title')}>
      <div id="audit">
        {model && model.enabled !== undefined && (
          <Form
            initialValues={model}
            defaultValidation={true}
            handleSubmit={handleSubmit}
            restValidationProps={{
              enableReinitialize: true,
              validationSchema: auditValidationSchema(t),
              innerRef: formRef,
            }}
          >
            <FormContext.Consumer>
              {(formContext: FormikProps<AuditModel>) => {
                const values = formContext.values;

                let applyButtonEnabled = formContext.dirty || !formContext.isSubmitting || !formContext.isValidating;
                const changed = changes(values, formContext.initialValues);
                delete changed.state; // state can change on its own
                // console.log('changes', formContext.dirty, changed)
                if (Object.keys(changed).length === 0 || formContext.isSubmitting || formContext.isValidating) {
                  applyButtonEnabled = false;
                }

                return (
                  <>
                    <div className="buttonRow">
                      {sessionStore.isAdmin() && (
                        <Button
                          variant="primary"
                          onClick={() => validateFormContext(formContext, t)}
                          disabled={!applyButtonEnabled}
                          state={buttonState}
                        >
                          {t('general.apply')}
                        </Button>
                      )}
                    </div>
                    <FormSection>
                      <DynamicContainer minColumns={3}>
                        <FormSwitch className="hai-mb-6" name="enabled" switchLabel={t('security.audit.form.audit')} />
                      </DynamicContainer>
                      {values.enabled && (
                        <>
                          <DynamicContainer minColumns={2}>
                            <FormTextfield mask name="server" title={t('security.audit.form.server')} required />
                            <FormSelect
                              name="transport"
                              title={t('security.audit.form.transport')}
                              options={Object.values(AuditTransport).map((transport: string) => ({
                                value: transport,
                                label: t(`security.audit.enum.transport.${transport}`),
                              }))}
                            />
                          </DynamicContainer>
                          <DynamicContainer minColumns={2}>
                            {values.transport !== 'UDP' && (
                              <FormSelect
                                name="trusted"
                                title={t('security.audit.form.trusted')}
                                options={Object.values(AuditTrustServer).map((trusted: string) => ({
                                  value: trusted,
                                  label: t(`security.audit.enum.trusted.${trusted}`),
                                }))}
                              />
                            )}
                            {values.transport !== 'UDP' && values.trusted === 'Self-signed' && (
                              <FormTextfield
                                mask
                                name="fingerprint"
                                title={t('security.audit.form.fingerprint')}
                                required
                              />
                            )}
                          </DynamicContainer>
                          <DynamicContainer minColumns={2}>
                            <FormValue
                              label={t('security.audit.form.connectionStatus')}
                              value={
                                errorReason ? `${toTitleCase(auditState)} (${errorReason})` : toTitleCase(auditState)
                              }
                              icon={iconState}
                            />
                          </DynamicContainer>
                          <br />
                        </>
                      )}
                    </FormSection>
                  </>
                );
              }}
            </FormContext.Consumer>
          </Form>
        )}
      </div>
    </SecurityTabContent>
  );
};
